import React, { useState, useCallback, useMemo, useRef } from "react";
import { Confirm } from "semantic-ui-react";
import ServerDataGrid from "../../../components/datagrid/ServerDataGrid";
import { CreateProductWizard } from "../../../components/forms/CreateProductWizard/CreateProductWizard";
import { AddListingToProduct } from "../../../components/forms/CreateProductWizard/AddListingToProduct";
import type { CreateMenuItem } from "../../../components/datagrid/types";
import { GridApiPremium } from "@mui/x-data-grid-premium";
import {
  NoteAdd as NoteAddIcon,
  AddLink as AddLinkIcon,
} from "@mui/icons-material";
import { columns } from "./columns";

export function ProductTablePage() {
  const internalApiRef = useRef<GridApiPremium | null>(null);

  const [displayProductWizard, setDisplayProductWizard] = useState(false);
  const [initialProduct, setInitialProduct] = useState<any>(null);
  const [productFormMode, setProductFormMode] = useState<
    "create" | "duplicate" | "edit"
  >("create");
  const [productFormPage, setProductFormPage] = useState(1);
  const [confirmCloseModalOpen, setConfirmCloseModalOpen] = useState(false);

  const [addListingProductData, setAddListingProductData] = useState<{
    productId: number;
    productTitle: string;
  } | null>(null);

  const handleCreateNew = useCallback(() => {
    setInitialProduct(null);
    setProductFormMode("create");
    setProductFormPage(1);
    setDisplayProductWizard(true);
  }, []);

  const closeCreateForm = useCallback((skipConfirmation?: boolean) => {
    if (skipConfirmation) {
      setDisplayProductWizard(false);
      setInitialProduct(null);
      setProductFormMode("create");
      setProductFormPage(1);
    } else {
      setConfirmCloseModalOpen(true);
    }
  }, []);

  const handleConfirmClose = useCallback(() => {
    setInitialProduct(null);
    setDisplayProductWizard(false);
    setConfirmCloseModalOpen(false);
    setProductFormMode("create");
    setProductFormPage(1);
  }, []);

  const handleOpenAddListing = useCallback(() => {
    const selected = Array.from(
      internalApiRef.current?.getSelectedRows().values() ?? []
    );
    if (selected.length !== 1) return;
    const row = selected[0];
    setAddListingProductData({
      productId: row.product?.product_id,
      productTitle: row.product?.product_title ?? "",
    });
  }, []);

  const handleCloseAddListing = useCallback((didCreate?: boolean) => {
    setAddListingProductData(null);
    if (didCreate) {
      internalApiRef.current?.dataSource?.fetchRows?.();
    }
  }, []);

  const createMenuItems = useMemo<CreateMenuItem[]>(
    () => [
      {
        label: "Create New Product",
        icon: <NoteAddIcon fontSize="small" />,
        onClick: handleCreateNew,
      },
      {
        label: "Add Listing to Product",
        icon: <AddLinkIcon fontSize="small" />,
        onClick: handleOpenAddListing,
        requiresRowSelection: true,
        disabledReason: "Select 1 row",
      },
    ],
    [handleCreateNew, handleOpenAddListing]
  );

  return (
    <>
      <Confirm
        open={confirmCloseModalOpen}
        content="Are you sure you want to close the create form? Any unsaved changes will be lost."
        onCancel={() => setConfirmCloseModalOpen(false)}
        onConfirm={handleConfirmClose}
      />

      {displayProductWizard && (
        <CreateProductWizard
          onClose={closeCreateForm}
          preselectedSupplyUnits={[]}
          initialProduct={initialProduct}
          initialPage={productFormPage}
          mode={productFormMode}
        />
      )}

      {addListingProductData && (
        <AddListingToProduct
          productId={addListingProductData.productId}
          productTitle={addListingProductData.productTitle}
          onClose={handleCloseAddListing}
        />
      )}

      <ServerDataGrid
        columns={columns}
        apiEndpoint="/datagrid/product"
        getRowId={(row) => row.product.product_id}
        tableName="product"
        enableCreate={true}
        enableSearch={true}
        pageSizeOptions={[25, 50, 100, 250, 500]}
        height={"calc(100vh - 52.6px)"}
        initialStateOverride={{
          sorting: { sortModel: [{ field: "product.created_at", sort: "desc" }] },
        }}
        createMenuItems={createMenuItems}
        onCreateClick={handleCreateNew}
        apiRef={internalApiRef}
      />
    </>
  );
}
